import { Wrench } from '@phosphor-icons/react/dist/ssr/Wrench'

/**
 * Load logo asset URLs at build time (not raw SVG markup) to keep JS payload slim.
 * Includes both `logos/tools/*` and root `logos/*.svg` assets.
 */
const modules = import.meta.glob<string>(
  '../../assets/logos/**/*.svg',
  {
    eager: true,
    import: 'default',
  },
)

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const baseNameFromPath = (fullPath: string) => {
  const raw = fullPath.split('/').pop() ?? ''
  try {
    return decodeURIComponent(raw).replace(/\.svg$/i, '').toLowerCase()
  } catch {
    return raw.replace(/\.svg$/i, '').toLowerCase()
  }
}

/** When the tool label does not match any file slug, map to an existing asset slug. */
const TOOL_SLUG_ALIASES: Record<string, string[]> = {
  'telegram-bot': ['telegram'],
  'self-hosted-database': ['nocodb'],
  'template-rendering': ['html'],
  'image-generation': ['nanabanana'],
  'agent-workflows': ['agent'],
}

const slugCandidatesForTool = (tool: string): string[] => {
  const key = slugify(tool)
  if (!key) {
    return []
  }
  return [key, ...(TOOL_SLUG_ALIASES[key] ?? [])]
}

const pathMatchesCandidateKey = (fullPath: string, candidateKey: string): boolean => {
  const base = baseNameFromPath(fullPath)
  if (base.includes('brand=')) {
    const m = base.match(/brand=([^,]+)/)
    if (!m?.[1]) {
      return false
    }
    return slugify(m[1].replace(/\+/g, ' ')) === candidateKey
  }
  return base === candidateKey || slugify(base) === candidateKey
}

const resolveLogoUrl = (tool: string): string | undefined => {
  const candidates = slugCandidatesForTool(tool)
  if (!candidates.length) {
    return undefined
  }
  const entries = Object.entries(modules)
  if (entries.length === 0) {
    return undefined
  }

  const sorted = [...entries].sort(([a], [b]) => {
    const ba = baseNameFromPath(a).includes('brand=') ? 1 : 0
    const bb = baseNameFromPath(b).includes('brand=') ? 1 : 0
    return ba - bb
  })

  for (const ck of candidates) {
    const hit = sorted.find(([path]) => pathMatchesCandidateKey(path, ck))
    if (hit) {
      return hit[1]
    }
  }
  return undefined
}

/** 24×24px mark inside a fixed 32×32 chip ([Figma 101:807](https://www.figma.com/design/5HYXNB9FTFb3Ttta8z0uGL/portfolio-update?node-id=101-807)). */
const svgHostClass =
  'pointer-events-none flex size-6 shrink-0 items-center justify-center ' +
  '[&>svg]:block [&>svg]:h-auto [&>svg]:w-auto [&>svg]:max-h-6 [&>svg]:max-w-6 [&>svg]:shrink-0 [&>svg]:object-contain'

/** Matches `desktopHoverLabelClass` in SiteNavigation; positioned below the chip instead of to the right. */
const toolHoverCaptionClass =
  'pointer-events-none absolute top-full left-1/2 z-10 mt-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-full bg-[#fafafa] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--foreground-muted)] shadow-[inset_0_0_0_1px_#e0e0e0] opacity-0 transition-[opacity,transform] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)] group-hover/tool:translate-y-0 group-hover/tool:opacity-100 group-focus-within/tool:translate-y-0 group-focus-within/tool:opacity-100 motion-reduce:transition-none'

const toolChipClass =
  'inline-flex size-8 shrink-0 cursor-default items-center justify-center rounded-full border border-[#e0e0e0] bg-transparent outline-none transition-[background-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:bg-[#fafafa] focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2'

type Props = {
  tools?: string[]
}

export const HeroToolLogos = ({ tools }: Props) => {
  if (!tools?.length) {
    return null
  }

  return (
    <ul className="flex list-none flex-wrap gap-2.5 p-0" aria-label="Tools used">
      {tools.map((tool) => {
        const logoUrl = resolveLogoUrl(tool)
        return (
          <li key={tool} className="group/tool relative">
            <span
              tabIndex={0}
              className={toolChipClass}
              role="img"
              aria-label={tool}
            >
              {logoUrl ? (
                <span className={svgHostClass} aria-hidden>
                  <img src={logoUrl} alt="" className="h-auto w-auto max-h-6 max-w-6 object-contain" loading="lazy" />
                </span>
              ) : (
                <Wrench className="size-6 shrink-0 text-[var(--foreground-muted)]" aria-hidden />
              )}
            </span>
            <span className={toolHoverCaptionClass} aria-hidden>
              {tool}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
